/**
 * User Service - Refactored with Unified Architecture
 * Migrated from axios to fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { QueryClient } from "@tanstack/react-query";

import { SupportedLocale } from "@/i18n";
import {
  ChangeUserPassword,
  IUserInfo,
  RealtorProfile,
} from "@/types/userInfo.type";
import { UserServiceProps } from "@/types/userService.type";

import {
  getUserEmail,
  saveUserEmail,
  saveUserInfo,
} from "./cookies/user-info.service";
import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";

// Enhanced type definitions for user responses
// Generic multilingual text type without hardcoded locale keys
type MultilingualText = string | Partial<Record<SupportedLocale, string>>;

export interface UserInfoResponse extends IUserInfo {
  _id?: string;
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  roles?: string[];

  // Multiple phone numbers
  phoneNumbers?: Array<{ phone: string; isValid: boolean }>;

  // Professional info
  workPlace?: string;
  experienceYears?: string;
  about?: MultilingualText;

  // Location
  // Social networks
  socialNetworks?: Array<{ type: string; url: string }>;

  // Professional preferences
  dealTypes?: string[];
  propertyTypes?: string[];

  // Settings
  profileSettings?: {
    language?: string;
    notifications?: boolean;
    emailMarketing?: boolean;
    showPhone?: boolean;
    showEmail?: boolean;
  };

  // Metadata
  registrationDate?: string;
  lastLogin?: string;
  isActive?: boolean;
  isVerified?: boolean;

  // Realtor options
  realtorOptions?: {
    dealTypes: Array<{ key: string; label: string }>;
    propertyTypes: Array<{ key: string; label: string }>;
  };
  avatar?: string;
  photoUrl?: string;
  isEmailVerified?: boolean;
  createdAt: string;
  updatedAt: string;
  realtorProfile?: RealtorProfile;
}

export interface FavoritePropertyResponse {
  properties: Array<{
    id: string;
    title: string;
    price: number;
    currency: string;
    images: string[];
    location: {
      city: string;
      region: string;
    };
    dealType: string;
    category: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OwnerPropertyResponse {
  properties: Array<{
    id: string;
    title: string;
    price: number;
    currency: string;
    status: string;
    views: number;
    createdAt: string;
    updatedAt: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FavoriteCountResponse {
  total: number;
  byDealType: Record<string, number>;
  byCategory: Record<string, number>;
}

export interface PhoneChangeResponse {
  message: string;
  success: boolean;
  phone: string;
}

export interface PasswordChangeResponse {
  message: string;
  success: boolean;
}

// Configuration constants
const DEFAULT_PAGE_SIZE = 12;
const DEFAULT_PAGE = 1;

/**
 * User Service Class
 * Handles all user-related operations
 * Single Responsibility: User profile and property management
 */
class UserService extends BaseService {
  constructor() {
    super({
      name: "user",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 2, // User operations should be responsive
      retryDelay: 1000,
    });
  }

  /**
   * Update realtor profile fields (Story-2)
   */
  async updateRealtorProfile(
    payload: RealtorProfile,
  ): Promise<{ message: string }> {
    try {
      return await this.patch<{ message: string }>(`/user/profile`, payload);
    } catch (error) {
      await this.handleError(error, "update realtor profile");
    }
  }

  async updateUserPassword(
    payload: ChangeUserPassword,
  ): Promise<{ message: string }> {
    try {
      return await this.patch<{ message: string }>(
        `/user/update-password`,
        payload,
      );
    } catch (error) {
      this.handleError(error, "update realtor profile");
      throw error;
    }
  }

  /**
   * Upload avatar image (multipart/form-data)
   */
  async uploadAvatar(file: File): Promise<{ imageUrl: string }> {
    const form = new FormData();
    form.append("file", file);

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/user/upload-avatar`,
        {
          method: "PATCH",
          body: form,
          credentials: "include",
        },
      );

      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
      }
      const data = await res.json();
      // Invalidate cached user-info if any (best-effort)
      try {
        await this.clearCache?.("user-info");
      } catch (error) {
        console.warn("Failed to clear cache after avatar upload:", error);
      }
      return data as { imageUrl: string };
    } catch (error) {
      return this.handleError(error, "upload avatar");
    }
  }

  // Removed getRealtorOptions - now included in getUserInfo

  /**
   * Get user information with automatic cookie storage
   */
  async getUserInfo(): Promise<UserInfoResponse | null> {
    try {
      const response = await this.get<UserInfoResponse>("/user/user-info");

      // Save email and user info to cookies as per original logic
      if (response.email) {
        saveUserEmail(response.email);
      }
      if (response) {
        saveUserInfo(response as IUserInfo);
      }

      this.logOperationSuccess("get user info", { hasEmail: !!response.email });
      return response;
    } catch (error) {
      // User info fetch can fail without breaking the app
      this.logger.warn("Failed to fetch user info, returning null", { error });
      return null;
    }
  }

  /**
   * Change user phone number
   */
  async changePhone(
    data: Pick<UserServiceProps, "phone">,
  ): Promise<PhoneChangeResponse> {
    this.validateRequired(data, ["phone"]);

    const userEmail = getUserEmail();
    if (!userEmail) {
      this.handleError(
        {
          message: "User email not found in cookies",
          code: "NO_EMAIL_COOKIE",
        },
        "phone change validation",
      );
    }

    const requestData = {
      phone: data.phone,
      email: userEmail,
    };

    try {
      return await this.patch<PhoneChangeResponse>(
        "/user/change-phone",
        requestData,
      );
    } catch (error) {
      return this.handleError(error, "change phone number");
    }
  }

  /**
   * Change user password
   */
  async changePassword(): Promise<PasswordChangeResponse> {
    try {
      return await this.patch<PasswordChangeResponse>("/user/change-password");
    } catch (error) {
      this.handleError(error, "change password");
    }
  }

  /**
   * Add property to user favorites
   */
  async addFavoriteProperty(
    data: Readonly<Pick<UserServiceProps, "propertyId">>,
  ): Promise<{ message: string; success: boolean }> {
    this.validateRequired(data, ["propertyId"]);

    const userEmail = getUserEmail();
    if (!userEmail) {
      this.handleError(
        {
          message: "User email not found in cookies",
          code: "NO_EMAIL_COOKIE",
        },
        "add favorite validation",
      );
    }

    const requestData = {
      propertyId: data.propertyId,
      email: userEmail,
    };

    try {
      return await this.patch<{ message: string; success: boolean }>(
        "/user/add-favorite-property",
        requestData,
      );
    } catch (error) {
      return this.handleError(error, "add favorite property");
    }
  }

  /**
   * Remove property from user favorites
   */
  async removeFavoriteProperty(
    data: Readonly<Pick<UserServiceProps, "propertyId">>,
  ): Promise<{ message: string; success: boolean }> {
    this.validateRequired(data, ["propertyId"]);

    const userEmail = getUserEmail();
    if (!userEmail) {
      this.handleError(
        {
          message: "User email not found in cookies",
          code: "NO_EMAIL_COOKIE",
        },
        "remove favorite validation",
      );
    }

    const bodyData = {
      propertyId: data.propertyId,
      email: userEmail,
    };

    try {
      return await this.delete<{ message: string; success: boolean }>(
        "/user/delete-favorite-property",
        bodyData,
      );
    } catch (error) {
      return this.handleError(error, "remove favorite property");
    }
  }

  /**
   * Get user's favorite property IDs (EXACT API match)
   */
  async showFavoritePropertyID() {
    const userEmail = getUserEmail();
    const response = await this.client.get("/user/show-favorite-property-id", {
      params: { email: userEmail as string },
    });

    return response.data;
  }

  /**
   * Get user's favorite properties with authentication (EXACT API match)
   */
  async showFavoritePropertyWithAuth(
    data?: Pick<UserServiceProps, "page" | "limit" | "dealType" | "userId">,
  ) {
    const userEmail = getUserEmail();
    const newData = { ...data, email: userEmail };
    const response = await this.client.get("/user/show-favorite-property", {
      params: newData,
    });

    return response.data;
  }

  /**
   * Get favorite properties without authentication (EXACT API match)
   */
  async showFavoritePropertyWithoutAuth(data: {
    ids: UserServiceProps["ids"];
    dealType?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await this.client.get(
      "/user/show-favorite-property-unauthorized",
      {
        params: data,
        paramsSerializer: {
          indexes: true,
        },
      },
    );

    return response.data;
  }

  /**
   * Get user's owned properties with authentication (UPDATED FOR STORY-5)
   * Uses property search API with creator/assignee filtering support
   */
  async showOwnerPropertyWithAuth(
    data?: Pick<
      UserServiceProps,
      "page" | "limit" | "userId" | "dealType" | "creatorId" | "assigneeId"
    >,
  ) {
    const userEmail = getUserEmail();
    // Map UI param `dealType` to API-required `category`
    const { dealType, ...rest } = data || {};
    const newData = {
      ...rest,
      ...(dealType ? { category: dealType } : {}),
      email: userEmail,
    };

    // Use property search endpoint for creator/assignee filtering support
    const response = await this.client.get("/property/all-property", {
      params: newData,
    });

    return response.data;
  }

  /**
   * Get user's owned property IDs (EXACT API match)
   */
  async showOwnerPropertyID() {
    const userEmail = getUserEmail();
    const response = await this.client.get("/user/show-owner-property-id", {
      params: { email: userEmail as string },
    });
    return response.data;
  }

  /**
   * Get favorite counts by category and deal type (EXACT API match)
   */
  async favoriteCounts(): Promise<Record<string, number>> {
    const response = await this.client.get("/user/favorite-counts");
    return response.data;
  }

  /**
   * Logout user (delegated to auth service but kept for compatibility)
   * Note: This should ideally be handled by the auth service
   */
  async logout(): Promise<{ message: string; success: boolean }> {
    try {
      const result = await this.client.post<{
        message: string;
        success: boolean;
      }>("/auth/logout");

      this.logOperationSuccess("logout");
      return result.data;
    } catch (error) {
      // Logout should not fail completely - log but don't throw
      this.logger.warn("Logout request failed, continuing with local cleanup", {
        error,
      });

      return {
        message: "Logged out locally",
        success: true,
      };
    }
  }

  /**
   * Prefetch owner properties for React Query (EXACT API match)
   */
  async prefetchOwnerProperties(
    queryClient: QueryClient,
    params?: Pick<UserServiceProps, "page" | "limit">,
  ) {
    await queryClient.prefetchQuery({
      queryKey: ["getOwnerProperties", params || { page: 1, limit: 12 }],
      queryFn: () => this.showOwnerPropertyWithAuth(params),
    });
  }

  /**
   * Prefetch owner property IDs for React Query (EXACT API match)
   */
  async prefetchOwnerPropertyIDs(queryClient: QueryClient) {
    await queryClient.prefetchQuery({
      queryKey: ["getOwnerPropertiesIDs"],
      queryFn: () => this.showOwnerPropertyID(),
    });
  }

  /**
   * Validate phone number format
   */
  private isValidPhone(phone: string): boolean {
    // Basic international phone number validation
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ""));
  }

  /**
   * Enhanced validation for user data
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // First run base validation
    super.validateRequired(params, requiredFields);

    // Additional user-specific validation
    if (params.phone && !this.isValidPhone(params.phone)) {
      this.handleError(
        {
          message: "Invalid phone number format",
          code: "INVALID_PHONE",
          details: { phone: params.phone },
        },
        "phone validation",
      );
    }

    if (params.propertyId) {
      // Ensure propertyId is valid (string or array of strings)
      const isValidPropertyId =
        typeof params.propertyId === "string" ||
        (Array.isArray(params.propertyId) &&
          params.propertyId.every((id) => typeof id === "string"));

      if (!isValidPropertyId) {
        this.handleError(
          {
            message: "Property ID must be a string or array of strings",
            code: "INVALID_PROPERTY_ID",
            details: { propertyId: params.propertyId },
          },
          "property ID validation",
        );
      }
    }

    if (params.page && (params.page < 1 || !Number.isInteger(params.page))) {
      this.handleError(
        {
          message: "Page number must be a positive integer",
          code: "INVALID_PAGE",
          details: { page: params.page },
        },
        "page validation",
      );
    }

    if (
      params.limit &&
      (params.limit < 1 ||
        params.limit > 100 ||
        !Number.isInteger(params.limit))
    ) {
      this.handleError(
        {
          message: "Limit must be an integer between 1 and 100",
          code: "INVALID_LIMIT",
          details: { limit: params.limit },
        },
        "limit validation",
      );
    }
  }

  /**
   * Custom health check for user service
   */
  async healthCheck() {
    try {
      // Quick health check by getting favorite counts (doesn't require user email)
      await this.getFavoriteCounts();

      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          favorites: "healthy",
          userInfo: "healthy",
          counts: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const userService = new UserService();

// Export for backward compatibility
export default userService;

// Export types for external use
export type {
  FavoriteCountResponse,
  FavoritePropertyResponse,
  OwnerPropertyResponse,
  PasswordChangeResponse,
  PhoneChangeResponse,
  UserInfoResponse,
};
